/**
 * flash.net.SharedObject backed by Platform storage (localStorage / JSON file / memory) as JSON.
 *
 * Storage key: `so:<localPath or "">:<name>`. Like Flash, getLocal returns the same instance for the
 * same name/path, and data is only persisted by flush() (Flash also flushes when the player exits:
 * the host should call `SharedObject.flushAll()` on shutdown / page unload).
 * Values are serialized with JSON (AMF-only types such as Date/ByteArray are not preserved).
 */
import { EventDispatcher } from '../events/EventDispatcher';
import { rt } from '../player/runtime';

/** Instances are cached per platform (so separate headless players do not share state). */
const instancesByPlatform = new WeakMap<object, Map<string, SharedObject>>();
const instancesNoPlatform = new Map<string, SharedObject>();
/** Fallback store when no platform is attached. */
const memoryStore = new Map<string, string>();

function instances(): Map<string, SharedObject> {
  const p = rt().platform;
  if (!p) return instancesNoPlatform;
  let m = instancesByPlatform.get(p);
  if (!m) instancesByPlatform.set(p, (m = new Map()));
  return m;
}

function storage() {
  const p = rt().platform;
  if (p) return p.storage;
  return {
    get: (k: string) => (memoryStore.has(k) ? (memoryStore.get(k) as string) : null),
    set: (k: string, v: string) => void memoryStore.set(k, v),
    remove: (k: string) => void memoryStore.delete(k),
  };
}

export class SharedObject extends EventDispatcher {
  /** @internal */ $key: string;
  /** @internal */ $data: Record<string, any>;
  /** @internal */ $name: string;
  objectEncoding: number;

  constructor() {
    super();
    this.$key = '';
    this.$data = {};
    this.$name = '';
    this.objectEncoding = 3;
  }

  static getLocal(name: string, localPath: string | null = null, _secure: boolean = false): SharedObject {
    if (!name || /[~%&\;:"',<>?#\s]/.test(name)) {
      throw new Error('Error #2134: Cannot create SharedObject.');
    }
    const key = `so:${localPath ?? ''}:${name}`;
    let so = instances().get(key);
    if (so) return so;
    so = new SharedObject();
    so.$key = key;
    so.$name = name;
    const raw = storage().get(key);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') so.$data = parsed;
      } catch {
        so.$data = {};
      }
    }
    instances().set(key, so);
    return so;
  }

  /** Flush every SharedObject created in this process (Flash does this on exit). */
  static flushAll(): void {
    for (const so of instances().values()) so.flush();
  }

  /** @internal Forget cached instances (tests). */
  static $reset(): void {
    instances().clear();
    memoryStore.clear();
  }

  get data(): Record<string, any> {
    return this.$data;
  }

  get size(): number {
    return JSON.stringify(this.$data).length;
  }

  flush(_minDiskSpace: number = 0): string {
    storage().set(this.$key, JSON.stringify(this.$data));
    return 'flushed';
  }

  clear(): void {
    this.$data = {};
    storage().remove(this.$key);
  }

  setProperty(name: string, value: any = null): void {
    this.$data[name] = value;
  }

  close(): void {
    /* local shared objects need no connection */
  }
}

export const SharedObjectFlushStatus = { FLUSHED: 'flushed', PENDING: 'pending' } as const;
